import { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { HelpfulQuestion } from '@buoy-components/user-feedback';
import { SecondaryCTAButton } from '@buoyhealth/common.buoy-button';

import { trackStructuredEvent } from 'src/analytics';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import SupplierCard from 'src/interviews/components/SupplierCard';
import {
  BinaryOutcome,
  NonBinaryOutcome,
} from 'src/interviews/components/TxocInterview/machine/types';
import SubmitButton from 'src/interviews/components/SubmitButton';
import { getCategory } from 'src/interviews/analytics';
import useInterview from 'src/interviews/hooks/useInterview';
import RichEndings from '../../Completion/components/RichEndings';
import Tooltip from '../../Completion/components/Tooltip';
import type { ISupplierCardConfig } from 'src/packages/polaris/types/interviews';
import useCompletion from '../hooks/useCompletion';
import { getSecondaryCtaConfig, getSupplierCardConfig } from './utils';
import { trackCtaClick, trackOutcome } from '../analytics';

type OutcomeMessageOptions = {
  isDenied?: boolean;
  treatment: string;
  outcomeCopy?: {
    header: string;
    explanation: string;
    outcome: string;
    color?: string;
  };
};

const OUTCOME_MESSAGE = {
  [BinaryOutcome.Approved]: ({ treatment, outcomeCopy }: OutcomeMessageOptions) => ({
    color: outcomeCopy?.color || '#719B9D',
    result: outcomeCopy?.header || `${treatment} is an appropriate option`,
    message: (
      <span>
        {outcomeCopy?.explanation ||
          `Based on your answers, ${treatment} appears to be a reasonable option. Please reach out to our clinician team to further discuss ${treatment}.`}
      </span>
    ),
  }),
  [BinaryOutcome.Denied]: ({ treatment, outcomeCopy }: OutcomeMessageOptions) => ({
    color: outcomeCopy?.color || '#DD9581',
    result: outcomeCopy?.header || `${treatment} is not an appropriate option`,
    message: (
      <span>
        {outcomeCopy?.explanation ||
          `Based on your answers, ${treatment} does not appear to be an appropriate option for you. Please reach out to our clinician team to further discuss your candidacy and alternatives to ${treatment}.`}
      </span>
    ),
  }),
  [NonBinaryOutcome.MoreInfo]: () => ({
    color: '',
    result: '',
    message: (
      <span>
        <div className="bp-pb-5">
          Thanks for providing your email. Check your inbox for more information.
        </div>
        <div> In the meantime, you can learn more about our related offering below.</div>
      </span>
    ),
  }),
};

type TxocCompletionProps = {
  triggerRestart: () => void;
  supplierCtaCardConfigs: ISupplierCardConfig[];
};

const TxocCompletion = ({ supplierCtaCardConfigs, triggerRestart }: TxocCompletionProps) => {
  const completion = useCompletion();
  const interview = useInterview();

  const customOutcomeCopy = completion.results.customOutcomeCopy;
  const outcome = completion.results.outcome;
  const treatment = completion.results.treatment;
  const isDenied = completion.results.isDenied;
  const treatmentDisplayName = treatment.abbreviation || treatment.name;

  const supplierCtaCardConfig = getSupplierCardConfig({ outcome, supplierCtaCardConfigs });
  const secondaryCtaConfig = getSecondaryCtaConfig({ isDenied: Boolean(isDenied) });
  useEffect(() => {
    trackOutcome({
      interviewToken: interview.token || '',
      outcome: completion.results?.outcome || null,
      partner: supplierCtaCardConfig?.header || '',
      treatment: completion.results?.treatment?.name || '',
    });
  }, [completion.results, interview.token, supplierCtaCardConfig]);

  if (!completion.results || outcome === NonBinaryOutcome.None) {
    return null;
  }

  const customOutcomeCopyConfig = customOutcomeCopy?.find((c) => c.outcome === outcome);

  const outcomeConfig = OUTCOME_MESSAGE[outcome]({
    treatment: treatmentDisplayName,
    outcomeCopy: customOutcomeCopyConfig,
  });
  // Handle click on supplier card CTA button
  const handleClick = () => {
    trackCtaClick({
      interviewToken: interview.token || '',
      partner: supplierCtaCardConfig?.header || '',
      treatment: treatmentDisplayName,
      ctaLink: supplierCtaCardConfig?.ctaButton?.url || '',
      ctaTitle: supplierCtaCardConfig?.ctaButton?.label || '',
    });
  };
  // Handle click on secondary CTA button
  const handleCtaClick = ({ ctaLink, ctaTitle }: { ctaLink: string; ctaTitle: string }) => {
    trackCtaClick({
      interviewToken: interview.token || '',
      partner: supplierCtaCardConfig?.header || '',
      treatment: treatmentDisplayName,
      ctaLink: ctaLink,
      ctaTitle: ctaTitle,
    });
    window.open(ctaLink, '_blank');
  };

  return (
    <Grid item>
      <Grid container direction="column" spacing={2}>
        {outcomeConfig.result && (
          <Grid item>
            <Paragraph className="bp-flex bp-items-center bp-justify-between">
              <div className="bp-flex bp-items-center bp-gap-2">
                <figure
                  className=" bp-h-4 bp-w-4 bp-rounded-lg"
                  style={{ backgroundColor: outcomeConfig.color }}
                />
                <span className="bp-font-bold">{outcomeConfig.result}</span>
              </div>
              <Tooltip content={outcomeConfig.result} />
            </Paragraph>
          </Grid>
        )}

        <Grid item>
          <Paragraph>{outcomeConfig.message}</Paragraph>
        </Grid>

        {supplierCtaCardConfig && (
          <Grid item className="!bp-pt-5">
            <SupplierCard
              supplierCtaCardConfig={supplierCtaCardConfig}
              handleCtaClick={handleClick}
            />
          </Grid>
        )}

        {!supplierCtaCardConfig && (
          <Grid item className="!bp-pt-5">
            <SecondaryCTAButton
              fullWidth
              className="!bp-rounded-[8px] !bp-px-[20px] !bp-py-[10px]"
              onClick={() =>
                handleCtaClick({
                  ctaLink: secondaryCtaConfig.link,
                  ctaTitle: secondaryCtaConfig.label,
                })
              }
            >
              {secondaryCtaConfig.label}
            </SecondaryCTAButton>
          </Grid>
        )}

        <Grid item>
          <HelpfulQuestion
            snowplowProps={{
              action: getCategory('txoc'),
              label: interview.token || '',
            }}
            trackingCallback={trackStructuredEvent}
            withBuoyTheme
          />
        </Grid>
      </Grid>
      <RichEndings interviewType="txoc" query={completion.results.treatment.name} />
      <SubmitButton onClick={triggerRestart} text="Restart" isRestart={true} />
    </Grid>
  );
};

export default TxocCompletion;
